/**
 * Runtime config injected by env-config.js, which index.html loads before this app
 * bootstraps. In the deployed container that file is regenerated at startup from the
 * API_BASE_URL environment variable — see docker-entrypoint.d/20-env-config.sh and
 * public/env-config.template.js. Declared here rather than left as an implicit global so
 * the lookup below is type-checked.
 */
declare global {
  interface Window {
    __env?: { apiBaseUrl?: string; apiKey?: string; clientId?: string };
  }
}

/** This app's own identity on a backend that may serve more than one frontend. */
const DEFAULT_CLIENT_ID = 'change-detection-ui';

/**
 * Resolves the API base URL at runtime instead of compiling one in, because this app has
 * no fixed backend host across environments (local dev, and however many deployed ones).
 * Falls back to the local API port only when env-config.js has nothing set — i.e. when
 * running `ng serve`/`ng build` straight from source rather than through the container,
 * where env-config.js is still the committed default (see public/env-config.js).
 */
function resolveApiBaseUrl(): string {
  const injected = typeof window !== 'undefined' ? window.__env?.apiBaseUrl : undefined;
  return injected && injected.trim().length > 0 ? injected.trim() : 'http://localhost:5066';
}

/**
 * The API key sent as X-Api-Key on every request to apiBaseUrl (see
 * core/api-key.interceptor.ts) — empty when none is configured, which matches the API's
 * own fail-open behaviour when it has no ApiKeys section either (typically local dev).
 *
 * This is a floor, not a guarantee: whatever ships here is visible in this browser app's
 * own network tab to anyone who opens it, same as any other client-side secret. It stops
 * casual/automated access to the API, not a determined user of this page. See
 * "Authentication" in the README for the full picture and the longer-term options.
 */
function resolveApiKey(): string {
  const injected = typeof window !== 'undefined' ? window.__env?.apiKey : undefined;
  return injected?.trim() ?? '';
}

/**
 * Sent as the mandatory X-Client-Id header on every request to apiBaseUrl (see
 * core/backend-headers.interceptor.ts). Unlike apiBaseUrl/apiKey this has a meaningful
 * committed default — it names *this frontend*, which rarely changes — rather than an
 * empty fallback: the backend rejects any request with no client id at all, so shipping
 * with nothing configured would break the app rather than degrade gracefully.
 */
function resolveClientId(): string {
  const injected = typeof window !== 'undefined' ? window.__env?.clientId : undefined;
  return injected && injected.trim().length > 0 ? injected.trim() : DEFAULT_CLIENT_ID;
}

export const environment = {
  production: true,

  /**
   * Base URL of ChangeDetection.Api. Never hard-code a deployed host here — set
   * API_BASE_URL on the container (or edit public/env-config.js for a non-Docker static
   * deploy) instead. See "Configuration" in the README.
   */
  apiBaseUrl: resolveApiBaseUrl(),

  /** See resolveApiKey() above. */
  apiKey: resolveApiKey(),

  /** See resolveClientId() above. */
  clientId: resolveClientId(),

  /**
   * Header branding. Driven from config so the app can be rebranded without touching
   * component code — see "Rebranding the app" in the README.
   */
  branding: {
    /** Shown as the main heading, and in the browser tab via index.html. */
    appName: 'Change Detection',

    /** One line under the name. Set to an empty string to hide it. */
    tagline: 'Satellite imagery comparison',

    /**
     * Path to the header logo, relative to the app root. The resource/ folder is served
     * from /branding (see the assets entry in angular.json), so 'branding/rta-logo.png'
     * resolves to ChangeDetection.UI/resource/rta-logo.png. Set to an empty string to
     * fall back to a plain text wordmark.
     *
     * The trimmed variant, not the square source: the header sizes a logo by height, so
     * the transparent padding around the mark would otherwise be charged against the one
     * dimension that is constrained — the supplied file is 32% mark and 68% air, which
     * left the logo eight pixels tall. Both carry transparency, which is what lets them
     * sit on the header without a white tile. The icons beside them in resource/ are the
     * same mark rendered onto white, for tabs and home screens where a transparent logo
     * would disappear.
     */
    logoUrl: 'branding/rta-logo-header.png'
  }
};
